<template>
  <WizardContainer
    width="792px"
    height="624px"
  >
    <WizardContent>
      <h4 class="mt-6 mb-8">
        Tell Us About Your Company
      </h4>
      <v-form
        ref="form"
        v-model="valid"
        class="form"
        @submit.prevent="submitForm"
      >
        <DpplTextField
          v-model="company"
          label="Company Name"
          :rules="companyRules"
          validate-on="blur"
          class="mb-1"
        />
        <DpplSelect
          v-model="ats"
          :items="atsSelectItems"
          label="Applicant Tracking System"
          :rules="atsRules"
          validate-on="blur"
          item-title="name"
          item-value="id"
          density="compact"
          variant="outlined"
          class="ats"
        />
        <DpplTextField
          v-model="url"
          class="mb-3"
          label="Link to Your Job Board"
          :rules="urlRules"
          validate-on="blur"
          :hint="urlHint"
        >
          <template #append-inner>
            <DpplTooltip
              top
              max-width="180px"
            >
              <template #activator="{ on }">
                <img
                  class="info-icon mr-1"
                  src="@/assets/icon_info.png"
                  alt="Info icon"
                  v-on="on"
                >
              </template>
              <span>
                Provide a link to an active job board hosted on your company’s site.
                For example: Datapeople.io/Careers
              </span>
            </DpplTooltip>
          </template>
        </DpplTextField>
        <DpplSignupFormButton
          class="mb-4"
          type="submit"
          block
          :loading="loading"
        >
          Submit
        </DpplSignupFormButton>
        <WizardFooter>
          <h5>
            Having Issues?
            <a
              :href="'mailto:hello@datapeople.io'
                + '?subject=Having%20issues%about%20your%20company%20on%20Datapeople'"
            >
              Contact Us
            </a>
          </h5>
        </WizardFooter>
      </v-form>
    </WizardContent>
  </WizardContainer>
</template>

<script>
import WizardContainer from '@/components/shared/WizardContainer.vue';
import WizardContent from '@/components/shared/WizardContent.vue';
import WizardFooter from '@/components/shared/WizardFooter.vue';
import { signupAtsOptions, signupSubmitAts } from '@/api/signup';
import validation from '@/helpers/validation';
import DpplSelect from '@/components/datapeople/DpplSelect.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import { mapActions } from 'pinia';
import { useWizard } from '@/stores/wizard/wizard';
import { useAppGate } from '@/stores/app/gate';

export default {
  name: 'AboutYourCompany',
  components: {
    WizardContainer,
    WizardContent,
    WizardFooter,
    DpplSelect,
    DpplTextField,
    DpplSignupFormButton,
    DpplTooltip,
  },
  emits: ['track-page-load'],
  data() {
    return {
      company: '',
      companyRules: [
        (v) => !validation.isEmpty(v) || 'Company name is required',
        (v) => (v && v.length <= 250) || 'Company name is too long',
      ],
      ats: '',
      atsRules: [
        (v) => !!v || 'ATS is required',
      ],
      url: '',
      urlRules: [
        (v) => !validation.isEmpty(v) || 'Url is required',
        (v) => validation.jobboardUrlBlacklist(v) || 'Please provide a valid URL to a job board'
          + ' hosted on your company’s website.',
        (v) => validation.url(v) || 'Url format is invalid',
        (v) => (v && v.length <= 250) || 'Url is too long',
      ],
      valid: false,
      loading: false,
      atsSelectItems: [
        /*
        'ADP',
        'Avature',
        'Brassring',
        'Cornerstone',
        'Google Hire',
        'Greenhouse',
        'GR8 People',
        'iCIMS',
        'JazzHR',
        'Jobscore',
        'Jobvite',
        'Kenexa',
        'Lever',
        'Newton',
        'PeopleFluent',
        'SelectMinds',
        'SilkRoad',
        'SmartRecruiters',
        'SuccessFactors',
        'Taleo',
        'Ultipro',
        'Workable',
        'Workday',
        'Other',
        'We don’t use an ATS',
        */
      ],
    };
  },
  computed: {
    urlHint() {
      return this.url && !validation.hasSubdomainOrPath(this.url)
        ? 'This URL does not appear to be a link to an active job board. Please confirm that'
          + ' you’re providing a link to your company’s open jobs.'
        : null;
    },
  },
  async created() {
    if (useAppGate().errorPageRoute !== 'about-your-company') {
      await this.$router.replace({ name: 'root' });
      return;
    }

    const getAtsList = async () => {
      const atsOptions = await signupAtsOptions();
      this.atsSelectItems = atsOptions;
    };

    getAtsList().catch((e) => {
      this.updateFlash({ message: e.message });
      this.loading = false;
    });
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'About Your Company' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupSubmitAts({
            company: this.company,
            url: this.url,
            ats: this.ats,
          });
          if (responseJSON.success === true) {
            this.$router.replace({ name: 'root' }).catch(() => {});
          }
        }
      } catch (e) {
        this.updateFlash({ template: 'contact-us', templateEmailSubject: 'Having issues about your company on Datapeople' });
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped lang="less">
.info-icon {
  height: 15px;
}

.form {
  width: 100%;
}

.ats {
  :deep(.v-input) {
      margin-top: 4px;
    }
  }
</style>
